import React, { useState, useEffect } from 'react';
import { Session } from '../types';
import { getCuisineEmoji, getPriceLevelString, formatRating } from '../utils';

interface ResultsScreenProps {
  session: Session;
  userName: string;
}

const ResultsScreen: React.FC<ResultsScreenProps> = ({ session, userName }) => {
  const [phase, setPhase] = useState<'drumroll' | 'reveal'>('drumroll');
  const [countdown, setCountdown] = useState(3);

  const matchedRestaurants = session.restaurants.filter(r =>
    session.matches.includes(r.id)
  );

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setTimeout(() => setPhase('reveal'), 500);
    }
  }, [countdown]);

  if (phase === 'drumroll') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 bg-dinder-blue">
        <div className="text-center">
          <div className="text-8xl mb-8">🥁</div>
          <h2 className="text-4xl font-extrabold text-white mb-4">Drumroll please...</h2>
          <p className="text-white/70 text-lg mb-10">Your matches are being revealed in</p>
          <div className="text-9xl font-extrabold text-dinder-pink">
            {countdown > 0 ? countdown : '🎉'}
          </div>
        </div>
      </div>
    );
  }

  if (matchedRestaurants.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 bg-dinder-blue">
        <div className="w-full max-w-sm text-center">
          <div className="text-8xl mb-6">😅</div>
          <h2 className="text-3xl font-extrabold text-white mb-3">No matches!</h2>
          <p className="text-white/70 mb-8">
            You two are tough customers. Try again and swipe right a little more generously!
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-dinder-pink text-white font-bold text-xl py-4 rounded-2xl transition-all active:scale-95"
          >
            Start Over
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-dinder-blue" style={{ minHeight: '100vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <div className="px-5 py-10 w-full max-w-sm mx-auto">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-3">🎉</div>
          <h2 className="text-4xl font-extrabold text-white mb-2">
            {matchedRestaurants.length === 1
              ? 'You have a match!'
              : `You have ${matchedRestaurants.length} matches!`}
          </h2>
          <p className="text-white/70">
            {matchedRestaurants.length === 1
              ? "Tonight's dinner is decided!"
              : 'Pick your favorite and enjoy!'}
          </p>
        </div>

        {/* Matched restaurants */}
        <div className="space-y-6 mb-8">
          {matchedRestaurants.map((restaurant, index) => (
            <div
              key={restaurant.id}
              className="bg-white/20 rounded-3xl overflow-hidden border border-white/20"
            >
              {/* Photo */}
              {restaurant.photoUrl ? (
                <img
                  src={restaurant.photoUrl}
                  alt={restaurant.name}
                  className="w-full h-44 object-cover"
                />
              ) : (
                <div className="w-full h-44 bg-gray-800 flex items-center justify-center">
                  <span className="text-6xl">{getCuisineEmoji(restaurant.cuisine)}</span>
                </div>
              )}

              {/* Info */}
              <div className="p-4 bg-dinder-orange rounded-b-3xl">
                <div className="flex items-start justify-between mb-1">
                  <h3 className="text-white font-extrabold text-xl flex-1 pr-2">{restaurant.name}</h3>
                  <span className="text-2xl">{getCuisineEmoji(restaurant.cuisine)}</span>
                </div>

                <div className="flex items-center gap-3 mb-2">
                  <span className="text-yellow-400 font-bold">★ {formatRating(restaurant.rating)}</span>
                  <span className="text-white/70 text-sm">({restaurant.reviewCount.toLocaleString()} reviews)</span>
                  <span className="text-white/80 text-sm">{getPriceLevelString(restaurant.priceLevel)}</span>
                </div>

                <p className="text-white/70 text-sm mb-3">{restaurant.address}</p>

                {restaurant.summary ? (
                  <p className="text-white/80 text-sm italic mb-4 leading-relaxed">
                    "{restaurant.summary}"
                  </p>
                ) : null}

                {/* Action buttons */}
                <div className="flex justify-around pt-3 border-t border-white/30">
                  {restaurant.googleMapsUrl ? (
                    <a
                      href={restaurant.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-1 active:opacity-70 transition-opacity"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/20">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                        </svg>
                      </div>
                      <span className="text-xs text-white/60">Directions</span>
                    </a>
                  ) : null}

                  {restaurant.websiteUrl ? (
                    <a
                     href={restaurant.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center gap-1 active:opacity-70 transition-opacity"
                    >
                      <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/20">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
                          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                        </svg>
                      </div>
                      <span className="text-xs text-white/60">Website</span>
                    </a>
                  ) : null}

                  <a
                    href={`https://www.google.com/search?q=${encodeURIComponent(restaurant.name + ' menu')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1 active:opacity-70 transition-opacity"
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/20">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 11l19-9-9 19-2-8-8-2z"/>
                      </svg>
                    </div>
                    <span className="text-xs text-white/60">Menu</span>
                  </a>
		  <a
		     href={`https://www.google.com/search?q=${encodeURIComponent(restaurant.name + ' ' + restaurant.address + ' reviews')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-1 active:opacity-70 transition-opacity"
                  >
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/20">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                    </div>
                    <span className="text-xs text-white/60">Reviews</span>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Start over */}
        <button
          onClick={() => window.location.reload()}
          className="w-full border border-white/30 text-white/80 font-semibold text-lg py-4 rounded-2xl transition-all active:scale-95 mb-6"
        >
          Start a New Session
        </button>

        <p className="text-white/40 text-xs text-center mb-10">Enjoy your meal! 🍽️</p>

      </div>
    </div>
  );
};

export default ResultsScreen;